import { useCallback, useEffect, useRef, useState } from "react";
import {
    RealtimeTranscriber,
    RealtimeTranscriberOptions,
    SpeechState,
} from "../transcription-button/RealtimeTranscriber";

export interface UseRealtimeTranscriberOptions
    extends RealtimeTranscriberOptions {
    /** If true, automatically adds a newline after each completed transcript */
    autoNewLines?: boolean;
    getAccessToken: () => Promise<string | undefined>;
}

export interface UseRealtimeTranscriberReturn {
    /** The accumulated transcript text */
    transcript: string;
    /** Live partial text for the utterance currently being spoken. Preview only — replaced, never appended, once the utterance completes. */
    partial: string;
    /** Whether the transcriber is currently running */
    isRunning: boolean;
    /** Current speech state from VAD */
    speechState: SpeechState;
    /** Audio level (0-1) for visualization */
    level: number;
    /** Any error that occurred */
    error: Error | null;
    /** Start the transcription session */
    start: () => Promise<void>;
    /** Stop the transcription session */
    stop: () => Promise<void>;
    /** Clear the transcript */
    clear: () => void;
    /** Set the transcript manually (useful for editing) */
    setTranscript: (text: string) => void;
}

export function useRealtimeTranscriber(
    options: UseRealtimeTranscriberOptions
): UseRealtimeTranscriberReturn {
    const { autoNewLines = true, getAccessToken, ...transcriberOptions } = options;

    const [transcript, setTranscript] = useState("");
    const [isRunning, setIsRunning] = useState(false);
    const [speechState, setSpeechState] = useState<SpeechState>("stopped");
    const [level, setLevel] = useState(0);
    const [error, setError] = useState<Error | null>(null);
    const [partial, setPartial] = useState("");

    const partialRef = useRef("");  // mirror, so stop() can read it synchronously
    const transcriberRef = useRef<RealtimeTranscriber | null>(null);
    const optionsRef = useRef(transcriberOptions);
    const autoNewLinesRef = useRef(autoNewLines);
    const getAccessTokenRef = useRef(getAccessToken);

    useEffect(() => {
        getAccessTokenRef.current = getAccessToken;
    }, [getAccessToken]);

    useEffect(() => {
        optionsRef.current = transcriberOptions;
    }, [transcriberOptions]);

    useEffect(() => {
        autoNewLinesRef.current = autoNewLines;
    }, [autoNewLines]);

    const start = useCallback(async () => {
        if (transcriberRef.current) {
            await transcriberRef.current.stop();
        }

        setError(null);
        const transcriber = new RealtimeTranscriber(
            getAccessTokenRef.current,
            optionsRef.current
        );
        transcriberRef.current = transcriber;

        transcriber.onDelta((delta) => {
            partialRef.current += delta;
            setPartial(partialRef.current);
        });

        transcriber.onTranscript((text) => {
            partialRef.current = "";
            setPartial("");                       // batched with the line below → one render
            setTranscript((prev) => {
                const newTranscript = prev + text;
                if (autoNewLinesRef.current && /[.!?:;]$/.test(newTranscript.trim()) && !newTranscript.endsWith("\n")) {
                    return newTranscript + "\n";
                }
                return newTranscript;
            });
        });

        transcriber.onSpeechState((state) => {
            setSpeechState(state);
        });

        transcriber.onLevel((l) => {
            setLevel(l);
        });

        transcriber.onError((err) => {
            setError(err);
        });

        try {
            await transcriber.start();
            setIsRunning(true);
        } catch (err) {
            setError(err instanceof Error ? err : new Error(String(err)));
            setIsRunning(false);
        }
    }, []);

    const stop = useCallback(async () => {
        setIsRunning(false);
        setLevel(0);
        setSpeechState("stopped");

        const leftover = partialRef.current;
        partialRef.current = "";
        setPartial("");
        if (leftover) setTranscript((prev) => prev + leftover);

        if (transcriberRef.current) {
            await transcriberRef.current.stop();
            transcriberRef.current = null;
        }
    }, []);

    const clear = useCallback(() => {
        setTranscript("");
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (transcriberRef.current) {
                transcriberRef.current.stop();
                transcriberRef.current = null;
            }
        };
    }, []);

    return {
        transcript,
        partial,
        isRunning,
        speechState,
        level,
        error,
        start,
        stop,
        clear,
        setTranscript
    };
}